/**
 * useDialog Hook - จัดการ Dialog State และ Actions
 * ใช้สำหรับแทน window.confirm() และ alert()
 */

import { useState, useCallback } from 'react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  ConfirmDialog,
  AlertDialog,
  PromptDialog,
  ConfirmDialogProps,
  AlertDialogProps,
  PromptDialogProps,
} from '@components/ui/Dialog';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

interface AlertOptions {
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
}

interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'info' | 'warning';
}

/**
 * useDialog Hook for Component State
 */
export const useDialog = () => {
  const [confirmConfig, setConfirmConfig] = useState<ConfirmOptions | null>(
    null
  );
  const [alertConfig, setAlertConfig] = useState<AlertOptions | null>(null);
  const [promptConfig, setPromptConfig] = useState<PromptOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [onConfirmCallback, setOnConfirmCallback] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const [onPromptCallback, setOnPromptCallback] = useState<
    ((value: string) => void | Promise<void>) | null
  >(null);

  // Show Confirm Dialog
  const showConfirm = useCallback(
    (
      options: ConfirmOptions,
      onConfirm: () => void | Promise<void>
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        setConfirmConfig(options);
        setOnConfirmCallback(() => async () => {
          setLoading(true);
          try {
            await onConfirm();
            resolve(true);
          } catch (error) {
            console.error('Confirm action error:', error);
            resolve(false);
          } finally {
            setLoading(false);
            setConfirmConfig(null);
            setOnConfirmCallback(null);
          }
        });
      });
    },
    []
  );

  // Show Alert Dialog
  const showAlert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertConfig(options);
      setTimeout(() => {
        resolve();
      }, 100);
    });
  }, []);

  // Show Prompt Dialog
  const showPrompt = useCallback(
    (
      options: PromptOptions,
      onConfirm: (value: string) => void | Promise<void>
    ): Promise<string | null> => {
      return new Promise((resolve) => {
        setPromptConfig(options);
        setOnPromptCallback(() => async (value: string) => {
          setLoading(true);
          try {
            await onConfirm(value);
            resolve(value);
          } catch (error) {
            console.error('Prompt action error:', error);
            resolve(null);
          } finally {
            setLoading(false);
            setPromptConfig(null);
            setOnPromptCallback(null);
          }
        });
      });
    },
    []
  );

  // Close Confirm Dialog
  const closeConfirm = useCallback(() => {
    setConfirmConfig(null);
    setOnConfirmCallback(null);
    setLoading(false);
  }, []);

  // Close Alert Dialog
  const closeAlert = useCallback(() => {
    setAlertConfig(null);
  }, []);

  // Close Prompt Dialog
  const closePrompt = useCallback(() => {
    setPromptConfig(null);
    setOnPromptCallback(null);
    setLoading(false);
  }, []);

  // Confirm Dialog Component
  const ConfirmDialogComponent = confirmConfig ? (
    <ConfirmDialog
      isOpen={!!confirmConfig}
      onClose={closeConfirm}
      onConfirm={() => onConfirmCallback?.()}
      title={confirmConfig.title}
      message={confirmConfig.message}
      confirmText={confirmConfig.confirmText}
      cancelText={confirmConfig.cancelText}
      variant={confirmConfig.variant}
      loading={loading}
    />
  ) : null;

  // Alert Dialog Component
  const AlertDialogComponent = alertConfig ? (
    <AlertDialog
      isOpen={!!alertConfig}
      onClose={closeAlert}
      title={alertConfig.title}
      message={alertConfig.message}
      variant={alertConfig.variant}
      confirmText={alertConfig.confirmText}
    />
  ) : null;

  // Prompt Dialog Component
  const PromptDialogComponent = promptConfig ? (
    <PromptDialog
      isOpen={!!promptConfig}
      onClose={closePrompt}
      onConfirm={(value) => onPromptCallback?.(value)}
      title={promptConfig.title}
      message={promptConfig.message}
      placeholder={promptConfig.placeholder}
      defaultValue={promptConfig.defaultValue}
      confirmText={promptConfig.confirmText}
      cancelText={promptConfig.cancelText}
      variant={promptConfig.variant}
      loading={loading}
    />
  ) : null;

  return {
    showConfirm,
    showAlert,
    showPrompt,
    ConfirmDialog: ConfirmDialogComponent,
    AlertDialog: AlertDialogComponent,
    PromptDialog: PromptDialogComponent,
  };
};

/**
 * Global Dialog Functions (Imperative API)
 * ใช้ได้เหมือน window.confirm() และ alert()
 */

// Global Confirm Function
export const confirm = async (
  message: string,
  title: string = 'ยืนยันการดำเนินการ',
  options?: Partial<ConfirmOptions>
): Promise<boolean> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      document.body.removeChild(container);
    };

    const handleConfirm = () => {
      resolve(true);
      cleanup();
    };

    const handleClose = () => {
      resolve(false);
      cleanup();
    };

    root.render(
      <ConfirmDialog
        isOpen={true}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={title}
        message={message}
        confirmText={options?.confirmText}
        cancelText={options?.cancelText}
        variant={options?.variant || 'warning'}
      />
    );
  });
};

// Global Alert Function
export const alert = async (
  message: string,
  variant: 'success' | 'error' | 'warning' | 'info' = 'info',
  title?: string
): Promise<void> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      document.body.removeChild(container);
      resolve();
    };

    root.render(
      <AlertDialog
        isOpen={true}
        onClose={cleanup}
        title={title}
        message={message}
        variant={variant}
      />
    );
  });
};

// Global Prompt Function
export const prompt = async (
  message: string,
  title: string = 'กรุณากรอกข้อมูล',
  options?: Partial<PromptOptions>
): Promise<string | null> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      document.body.removeChild(container);
    };

    const handleConfirm = (value: string) => {
      resolve(value);
      cleanup();
    };

    const handleClose = () => {
      resolve(null);
      cleanup();
    };

    root.render(
      <PromptDialog
        isOpen={true}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={title}
        message={message}
        placeholder={options?.placeholder}
        defaultValue={options?.defaultValue}
        confirmText={options?.confirmText}
        cancelText={options?.cancelText}
        variant={options?.variant || 'info'}
      />
    );
  });
};

export default useDialog;
